/**
 * /profile — account settings page (industry-standard pattern).
 *
 * Sections (top → bottom):
 *   1. Account header        avatar + name + email + status badge
 *   2. Personal information  labelled form (first/last name editable,
 *                            email locked); section-level Save button
 *                            enables only when fields are dirty
 *   3. Access                roles with admin-implies-everything
 *                            collapse logic
 *   4. Security              password row with "Update password" CTA
 *
 * Each section is its own card with a clear section header so the
 * hierarchy reads top-down. No hover-to-edit affordances — always-
 * visible form inputs, GitHub/Linear/Vercel style.
 */

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CampaignIcon from '@mui/icons-material/Campaign';
import HistoryIcon from '@mui/icons-material/History';
import KeyIcon from '@mui/icons-material/Key';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { updateMeRequest } from '../api/auth';
import { SiteLayout } from '../components/SiteLayout';
import { useAuth } from '../auth/useAuth';
import type { RolePublic } from '../api/auth';
import { FieldProfileSection } from './field/FieldProfileSection';

const INK = '#0f0a07';
const IVORY = '#faf6ee';
const IVORY_LIGHT = '#fdf9f0';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

const STATUS_META: Record<string, { label: string; bg: string; fg: string; dot: string }> = {
  active: { label: 'Active', bg: 'rgba(34, 134, 90, 0.12)', fg: '#22865a', dot: '#22865a' },
  pending: { label: 'Pending', bg: 'rgba(187, 140, 63, 0.12)', fg: '#a07530', dot: '#bb8c3f' },
  disabled: { label: 'Disabled', bg: 'rgba(168, 107, 30, 0.12)', fg: '#a86b1e', dot: '#a86b1e' },
  rejected: { label: 'Rejected', bg: 'rgba(178, 47, 47, 0.12)', fg: '#b22f2f', dot: '#b22f2f' },
};

const ROLE_ICON: Record<string, typeof VerifiedIcon> = {
  admin: SecurityIcon,
  depletions: LocalShippingIcon,
  marketing: CampaignIcon,
  field_rep: MapOutlinedIcon,
};

const MotionBox = motion.create(Box);

export function ProfilePage() {
  const { user, isReady, refreshUser } = useAuth();
  const navigate = useNavigate();

  if (!isReady) return <FrameWithSpinner />;
  if (!user) {
    return (
      <Frame>
        <Container maxWidth="md" sx={{ pt: 6, position: 'relative', zIndex: 1 }}>
          <Alert severity="error">Not signed in.</Alert>
        </Container>
      </Frame>
    );
  }

  const initial = ((user.first_name || user.email)[0] ?? '?').toUpperCase();
  const statusInfo = STATUS_META[user.status] ?? {
    label: user.status,
    bg: 'rgba(0,0,0,0.08)',
    fg: '#666',
    dot: '#999',
  };

  return (
    <Frame>
      <Container
        maxWidth="md"
        sx={{
          pt: { xs: 4, md: 6 },
          pb: { xs: 5, md: 8 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* ─── 1. Header ──────────────────────────────────────── */}
        <MotionBox
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.42, ease: 'easeOut' }}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            gap: { xs: 2.5, sm: 3 },
            mb: 4,
          }}
        >
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: -5,
                borderRadius: '50%',
                background: `conic-gradient(from 135deg, ${GOLD_LIGHT}, transparent 25%, ${GOLD} 50%, transparent 75%, ${GOLD_LIGHT})`,
                opacity: 0.4,
                filter: 'blur(2px)',
              }}
            />
            <Box
              sx={{
                position: 'relative',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
                color: INK,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_INTER,
                fontSize: 32,
                fontWeight: 700,
                boxShadow:
                  'inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 22px rgba(187, 140, 63, 0.20)',
              }}
            >
              {initial}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontWeight: 700,
                fontSize: { xs: 24, md: 30 },
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
                color: INK,
              }}
            >
              {user.first_name} {user.last_name}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontFamily: FONT_INTER,
                fontSize: 14.5,
                color: 'rgba(15, 10, 7, 0.6)',
              }}
            >
              {user.email}
            </Typography>

            <Box sx={{ mt: 1.5, display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              <StatusPill
                label={statusInfo.label}
                bg={statusInfo.bg}
                fg={statusInfo.fg}
                dot={statusInfo.dot}
              />
              <MetaPill
                icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
                label="Member Since"
                value={new Date(user.created_at).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              />
              {user.last_login_at && (
                <MetaPill
                  icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
                  label="Last Sign-in"
                  value={new Date(user.last_login_at).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                />
              )}
            </Box>
          </Box>
        </MotionBox>

        <Stack spacing={2.5}>
          {/* ─── 2. Personal information ───────────────────── */}
          <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, delay: 0.06, ease: 'easeOut' }}
          >
            <SectionCard
              title="Personal Information"
              description="Your name as it appears across the dashboard."
            >
              <PersonalInfoForm
                firstName={user.first_name}
                lastName={user.last_name}
                email={user.email}
                onSaved={() => refreshUser()}
              />
            </SectionCard>
          </MotionBox>

          {/* ─── 2b. Regional Sales profile ─────────────────────────
              Visible for regional sales reps (they need to edit their
              address) AND for admins (so they can pre-fill their
              address before granting themselves the Regional Sales
              Representative role).  Regular section users (distribution
              / depletions / marketing) don't see it. */}
          {user.roles.some((r) => r.name === 'field_rep' || r.name === 'admin') && (
            <MotionBox
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 0.09, ease: 'easeOut' }}
            >
              <SectionCard
                title="Sales Representative Profile"
                description="Home address and phone for your Regional Sales Representative role. Used to plan routes once you're assigned a territory."
              >
                <FieldProfileSection />
              </SectionCard>
            </MotionBox>
          )}

          {/* ─── 3. Access ─────────────────────────────────── */}
          <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, delay: 0.12, ease: 'easeOut' }}
          >
            <SectionCard
              title="Access"
              description="The sections of the platform you can use. An administrator manages these."
            >
              {user.roles.length === 0 ? (
                <Alert severity="warning" sx={{ mt: 0.5 }}>
                  No roles assigned. Contact your administrator.
                </Alert>
              ) : (
                <AccessBreakdown roles={user.roles} />
              )}
            </SectionCard>
          </MotionBox>

          {/* ─── 4. Security ────────────────────────────────── */}
          <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, delay: 0.18, ease: 'easeOut' }}
          >
            <SectionCard
              title="Security"
              description="Sign-in credentials and account-protection settings."
            >
              <Stack
                divider={<Box sx={{ borderBottom: '1px solid rgba(15, 10, 7, 0.06)' }} />}
                spacing={0}
              >
                <SecurityRow
                  icon={<KeyIcon sx={{ fontSize: 20 }} />}
                  title="Password"
                  description="Used to sign in. Update it any time."
                  action={{
                    label: 'Update password',
                    onClick: () => navigate('/change-password'),
                    primary: true,
                  }}
                />

                <SecurityRow
                  icon={<HistoryIcon sx={{ fontSize: 20 }} />}
                  title="Recent Sign-in"
                  description={
                    user.last_login_at
                      ? `Last signed in on ${new Date(user.last_login_at).toLocaleString(
                          undefined,
                          {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          },
                        )}`
                      : 'No previous sign-ins recorded.'
                  }
                  status={{ label: 'You are signed in', tone: 'success' }}
                />
              </Stack>
            </SectionCard>
          </MotionBox>
        </Stack>
      </Container>
    </Frame>
  );
}

// ---------------------------------------------------------------------
// Personal-information form
// ---------------------------------------------------------------------

interface PersonalInfoFormProps {
  firstName: string;
  lastName: string;
  email: string;
  onSaved: () => void;
}

function PersonalInfoForm({ firstName, lastName, email, onSaved }: PersonalInfoFormProps) {
  // MUI's "small" controls are ~30px tall and its inputs ~27px — under
  // the ~40px a thumb needs. Full size on a phone, compact on the
  // pointer-driven screens where the density is worth having.
  const muiTheme = useTheme();
  const compactControls = useMediaQuery(muiTheme.breakpoints.up('sm'));

  const [firstDraft, setFirstDraft] = useState(firstName);
  const [lastDraft, setLastDraft] = useState(lastName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);

  // Re-sync drafts if parent props change (e.g. via /me refetch).
  useEffect(() => {
    setFirstDraft(firstName);
    setLastDraft(lastName);
  }, [firstName, lastName]);

  const dirty = firstDraft.trim() !== firstName.trim() || lastDraft.trim() !== lastName.trim();
  const valid = firstDraft.trim().length > 0 && lastDraft.trim().length > 0;

  const reset = () => {
    setFirstDraft(firstName);
    setLastDraft(lastName);
    setError(null);
  };

  const save = async () => {
    if (!dirty || !valid) return;
    setSaving(true);
    setError(null);
    try {
      await updateMeRequest({
        first_name: firstDraft.trim(),
        last_name: lastDraft.trim(),
      });
      onSaved();
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 1800);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      {/* First name + Last name side-by-side */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <FieldGroup label="First name">
          <TextField
            value={firstDraft}
            onChange={(e) => setFirstDraft(e.target.value)}
            size={compactControls ? 'small' : 'medium'}
            disabled={saving}
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
        </FieldGroup>
        <FieldGroup label="Last name">
          <TextField
            value={lastDraft}
            onChange={(e) => setLastDraft(e.target.value)}
            size={compactControls ? 'small' : 'medium'}
            disabled={saving}
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
        </FieldGroup>
      </Box>

      {/* Email — locked */}
      <FieldGroup
        label="Email address"
        helper="Email is your sign-in identifier. To change it, contact an administrator."
      >
        <TextField
          value={email}
          size={compactControls ? 'small' : 'medium'}
          fullWidth
          disabled
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(15, 10, 7, 0.75)',
              fontFamily: 'monospace',
            },
          }}
        />
      </FieldGroup>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Footer with state + actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 1.5,
          borderTop: '1px solid rgba(15, 10, 7, 0.08)',
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            color: savedToast ? '#22865a' : dirty ? GOLD : 'rgba(15, 10, 7, 0.4)',
            transition: 'color 200ms ease',
          }}
        >
          {savedToast ? '✓ Saved' : dirty ? 'Unsaved changes' : 'Up to date'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={reset}
            disabled={!dirty || saving}
            size={compactControls ? 'small' : 'medium'}
            sx={{ fontFamily: FONT_INTER, fontSize: 12.5, textTransform: 'none' }}
          >
            Discard
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || !valid || saving}
            variant="contained"
            size={compactControls ? 'small' : 'medium'}
            sx={{
              background:
                dirty && valid
                  ? `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`
                  : undefined,
              color: dirty && valid ? INK : undefined,
              fontFamily: FONT_INTER,
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              px: 2,
              boxShadow: 'none',
              '&:hover':
                dirty && valid
                  ? {
                      background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
                      boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
                    }
                  : undefined,
            }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <SiteLayout subtitle="Account" bgcolor={IVORY}>
      {/* Paper-grain texture overlay — covers the content area
          without leaking into the Header or Footer. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
          backgroundSize: '200px 200px',
          mixBlendMode: 'multiply',
          opacity: 0.05,
        }}
      />
      {children}
    </SiteLayout>
  );
}

function FrameWithSpinner() {
  return (
    <Frame>
      <Container
        maxWidth="md"
        sx={{ pt: 8, display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}
      >
        <CircularProgress sx={{ color: GOLD }} />
      </Container>
    </Frame>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: IVORY_LIGHT,
        border: '1px solid rgba(187, 140, 63, 0.18)',
        borderRadius: 2,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, md: 3 },
          pt: { xs: 2.25, md: 2.5 },
          pb: { xs: 1.75, md: 2 },
          borderBottom: '1px solid rgba(15, 10, 7, 0.06)',
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 15.5,
            fontWeight: 600,
            color: INK,
            letterSpacing: '-0.005em',
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            fontFamily: FONT_INTER,
            fontSize: 13,
            color: 'rgba(15, 10, 7, 0.55)',
          }}
        >
          {description}
        </Typography>
      </Box>
      <Box sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2.25, md: 2.5 } }}>{children}</Box>
    </Box>
  );
}

function FieldGroup({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12,
          fontWeight: 600,
          color: 'rgba(15, 10, 7, 0.75)',
          letterSpacing: '0.02em',
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      {children}
      {helper && (
        <Typography
          sx={{
            mt: 0.75,
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            color: 'rgba(15, 10, 7, 0.5)',
            lineHeight: 1.5,
          }}
        >
          {helper}
        </Typography>
      )}
    </Box>
  );
}

function StatusPill({
  label,
  bg,
  fg,
  dot,
}: {
  label: string;
  bg: string;
  fg: string;
  dot: string;
}) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.4,
        borderRadius: 999,
        bgcolor: bg,
        color: fg,
        fontFamily: FONT_INTER,
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: dot,
        }}
      />
      {label}
    </Box>
  );
}

function MetaPill({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: 0.4,
        borderRadius: 999,
        bgcolor: 'rgba(15, 10, 7, 0.04)',
        border: '1px solid rgba(15, 10, 7, 0.08)',
      }}
    >
      <Box sx={{ display: 'flex', color: 'rgba(15, 10, 7, 0.5)', mr: 0.25 }}>{icon}</Box>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 11.5,
          color: 'rgba(15, 10, 7, 0.55)',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 11.5,
          color: 'rgba(15, 10, 7, 0.78)',
          fontWeight: 600,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Access breakdown
//
// Instead of just listing role names, show concretely what the user
// can DO. Admin role expands to every capability on the platform.
// Each non-admin role expands to the section(s) it grants.
// ---------------------------------------------------------------------

interface Capability {
  key: string;
  icon: typeof VerifiedIcon;
  label: string;
  description: string;
}

const CAPABILITY: Record<string, Capability> = {
  depletions: {
    key: 'depletions',
    icon: LocalShippingIcon,
    label: 'Depletions',
    description: 'Retail pull-through, account heatmap, follow-up tracker.',
  },
  marketing: {
    key: 'marketing',
    icon: CampaignIcon,
    label: 'Marketing',
    description: 'Competitor watch, social patterns, content intelligence.',
  },
  users: {
    key: 'users',
    icon: VerifiedIcon,
    label: 'User management',
    description: 'Create users, approve sign-ups, enable / disable accounts.',
  },
  roles: {
    key: 'roles',
    icon: SecurityIcon,
    label: 'Role management',
    description: 'Add custom roles and manage the role catalog.',
  },
  audit: {
    key: 'audit',
    icon: ScheduleIcon,
    label: 'Audit log',
    description: 'Forensic record of every sign-in, role change, and admin action.',
  },
  field_rep: {
    key: 'field_rep',
    icon: MapOutlinedIcon,
    label: 'Regional Sales',
    description: "Today's prioritized accounts, visit notes, and territory follow-up.",
  },
};

const ALL_ADMIN_CAPS = ['depletions', 'marketing', 'users', 'roles', 'audit'];

function AccessBreakdown({ roles }: { roles: RolePublic[] }) {
  const adminRole = roles.find((r) => r.name === 'admin');

  // Build the (role → capabilities[]) view. Admin collapses everyone
  // else into its full-access expansion.
  const groups: { role: RolePublic; caps: Capability[] }[] = adminRole
    ? [
        {
          role: adminRole,
          caps: ALL_ADMIN_CAPS.map((k) => CAPABILITY[k]).filter((c): c is Capability => Boolean(c)),
        },
      ]
    : roles.map((r) => {
        const cap = CAPABILITY[r.name];
        return { role: r, caps: cap ? [cap] : [] };
      });

  return (
    <Stack spacing={2}>
      {groups.map(({ role, caps }) => (
        <RoleAccessGroup key={role.id} role={role} caps={caps} fullAccess={!!adminRole} />
      ))}
    </Stack>
  );
}

function RoleAccessGroup({
  role,
  caps,
  fullAccess,
}: {
  role: RolePublic;
  caps: Capability[];
  fullAccess: boolean;
}) {
  const Icon = ROLE_ICON[role.name] ?? VerifiedIcon;
  // Three layout modes:
  //   * No caps     — header only.  Prevents the empty "SECTION" box
  //                    we'd otherwise render when CAPABILITY has no
  //                    entry for this role's name.
  //   * Single cap  — render a single tight "grant" chip below the
  //                    header.  Avoids nested-box-within-box clutter.
  //   * Multiple    — full grid (admin's "Full access" expansion).
  const hasMany = caps.length > 1;
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'rgba(255, 255, 255, 0.45)',
        border: '1px solid rgba(187, 140, 63, 0.22)',
        borderRadius: 1.5,
        overflow: 'hidden',
        transition: 'box-shadow 0.18s ease, border-color 0.18s ease',
        '&:hover': {
          borderColor: 'rgba(187, 140, 63, 0.36)',
          boxShadow: '0 2px 8px rgba(187, 140, 63, 0.08)',
        },
      }}
    >
      {/* Gold accent rail on the left — subtle craft detail */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 3,
          bgcolor: fullAccess ? '#22865a' : GOLD,
          opacity: 0.85,
        }}
        aria-hidden
      />
      {/* Role header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.25,
            bgcolor: fullAccess ? 'rgba(34, 134, 90, 0.12)' : 'rgba(187, 140, 63, 0.14)',
            color: fullAccess ? '#22865a' : GOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 14.5,
                fontWeight: 700,
                color: INK,
                lineHeight: 1.2,
                letterSpacing: '-0.005em',
              }}
            >
              {fullAccess ? 'Administrator' : role.display_name}
            </Typography>
            {role.is_system && (
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: GOLD,
                  bgcolor: 'rgba(187, 140, 63, 0.12)',
                  border: '1px solid rgba(187, 140, 63, 0.32)',
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 0.5,
                }}
              >
                System
              </Typography>
            )}
            {fullAccess && (
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#22865a',
                  bgcolor: 'rgba(34, 134, 90, 0.10)',
                  border: '1px solid rgba(34, 134, 90, 0.32)',
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 0.5,
                }}
              >
                Full access
              </Typography>
            )}
          </Box>
          {role.description && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 12.5,
                color: 'rgba(15, 10, 7, 0.62)',
                mt: 0.35,
                lineHeight: 1.5,
              }}
            >
              {role.description}
            </Typography>
          )}
        </Box>
      </Box>

      {caps.length > 0 && (
        <>
          {/* Dashed gold separator — visually attaches the grants
              strip to the role header without a heavy border */}
          <Box
            sx={{
              mx: 2,
              borderTop: '1px dashed rgba(187, 140, 63, 0.32)',
            }}
            aria-hidden
          />
          <Box sx={{ px: 2, pt: 1.25, pb: 1.5 }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: 'rgba(15, 10, 7, 0.42)',
                mb: 1,
              }}
            >
              {fullAccess ? 'Includes' : 'Grants access to'}
            </Typography>
            {hasMany ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: caps.length <= 4 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                  },
                  gap: 0.5,
                }}
              >
                {caps.map((c) => (
                  <CapabilityRow key={c.key} capability={c} />
                ))}
              </Box>
            ) : (
              <SingleCapabilityChip capability={caps[0]!} />
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

// Tighter inline treatment for roles that grant exactly one section.
// Avoids the visual weight of a full CapabilityRow for what's really
// just a single named grant.
function SingleCapabilityChip({ capability }: { capability: Capability }) {
  const Icon = capability.icon;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.25,
        py: 0.85,
        borderRadius: 1,
        bgcolor: 'rgba(187, 140, 63, 0.06)',
        border: '1px solid rgba(187, 140, 63, 0.22)',
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: 0.75,
          bgcolor: 'rgba(187, 140, 63, 0.14)',
          color: GOLD,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 14 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 13,
            fontWeight: 700,
            color: INK,
            lineHeight: 1.2,
          }}
        >
          {capability.label}
        </Typography>
        <Typography
          sx={{
            mt: 0.15,
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            color: 'rgba(15, 10, 7, 0.6)',
            lineHeight: 1.4,
          }}
        >
          {capability.description}
        </Typography>
      </Box>
    </Box>
  );
}

function CapabilityRow({ capability }: { capability: Capability }) {
  const Icon = capability.icon;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        px: 1.25,
        py: 1.25,
        borderRadius: 1,
        transition: 'background-color 160ms ease',
        '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.05)' },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 0.75,
          bgcolor: 'rgba(187, 140, 63, 0.10)',
          color: GOLD,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 15 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 13,
            fontWeight: 600,
            color: INK,
            lineHeight: 1.2,
          }}
        >
          {capability.label}
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            lineHeight: 1.45,
            color: 'rgba(15, 10, 7, 0.55)',
          }}
        >
          {capability.description}
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Security row
//
// Vertically-stacked list-item that represents one security feature
// (password, 2FA, recent activity, etc.). Each row has icon + title +
// description and either an action button, a status badge, or both.
// ---------------------------------------------------------------------

interface SecurityRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  status?: { label: string; tone: 'success' | 'muted' | 'warning' };
  action?: {
    label: string;
    onClick: () => void;
    primary?: boolean;
    disabled?: boolean;
  };
}

const STATUS_TONE: Record<
  'success' | 'muted' | 'warning',
  { bg: string; fg: string; dot: string }
> = {
  success: { bg: 'rgba(34, 134, 90, 0.10)', fg: '#22865a', dot: '#22865a' },
  warning: { bg: 'rgba(187, 140, 63, 0.10)', fg: '#a07530', dot: '#bb8c3f' },
  muted: { bg: 'rgba(15, 10, 7, 0.05)', fg: 'rgba(15, 10, 7, 0.55)', dot: 'rgba(15, 10, 7, 0.4)' },
};

function SecurityRow({ icon, title, description, status, action }: SecurityRowProps) {
  const muiTheme = useTheme();
  const compactControls = useMediaQuery(muiTheme.breakpoints.up('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'center' },
        gap: 2,
        py: 2,
        px: 0.5,
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: 'rgba(187, 140, 63, 0.10)',
          color: GOLD,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      {/* Title + description */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 14,
              fontWeight: 600,
              color: INK,
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          {status && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.9,
                py: 0.2,
                borderRadius: 999,
                bgcolor: STATUS_TONE[status.tone].bg,
                color: STATUS_TONE[status.tone].fg,
              }}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor: STATUS_TONE[status.tone].dot,
                }}
              />
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                }}
              >
                {status.label}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography
          sx={{
            mt: 0.4,
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            color: 'rgba(15, 10, 7, 0.6)',
            lineHeight: 1.45,
          }}
        >
          {description}
        </Typography>
      </Box>

      {/* Action */}
      {action && (
        <Button
          onClick={action.onClick}
          disabled={action.disabled}
          variant={action.primary ? 'contained' : 'outlined'}
          size={compactControls ? 'small' : 'medium'}
          sx={{
            flexShrink: 0,
            fontFamily: FONT_INTER,
            fontWeight: 600,
            fontSize: 12,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            px: 2,
            ...(action.primary && {
              background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
              color: INK,
              boxShadow: 'none',
              '&:hover': {
                background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
                boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
              },
            }),
            ...(!action.primary && {
              borderColor: 'rgba(187, 140, 63, 0.4)',
              color: GOLD,
              '&:hover': {
                borderColor: GOLD,
                bgcolor: 'rgba(187, 140, 63, 0.06)',
              },
              '&.Mui-disabled': {
                borderColor: 'rgba(15, 10, 7, 0.12)',
                color: 'rgba(15, 10, 7, 0.35)',
              },
            }),
          }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
}
